import { Box, Typography } from '@mui/material';
import { facilityColors, legendFontSize, groupColors, fertilizerColors, tooltipContainer, tooltipText, agrochemicalsColors } from '../constants/graph-constants';

/** 
* Formats and reduces the font size of the colour legend in the bar graph
* @param {number | string} value This is the value wrapped inside of the returned < span > element 
* @returns {JSX.Element} A < span > html element
*/
export function formatLegend(value) {
    return <span style={{ fontSize: legendFontSize }}>{value}</span>;
}

/**
 * Formats the ticker so that it introduces 'K' at 1,000 and 'M' at 1,000,000 to truncate graph y-axis
 * labels
 * @param {number} value Value passed in from Recharts
 */
export function formatTicker(value) {
    // thousands
    if (value >= 1000 && value < 1e6) {
        return (value / 1000) + 'K'
    }
    // millions
    else if (value >= 1e6 && value < 1e9) {
        return (value / 1e6) + 'M'
    }
    // billions
    else if (value >= 1e9) {
        return (value / 1e9) + 'B'
    }
    else return value
}

/**
 * Matches a group / facility name to a colour value and returns a matched color.
 * - Note: A group / facility COULD have the same name, so we use {variant} to separate between the two.
 * @param {string} name 
 * @param {string} variant "facility" || "group" 
 * @returns {string}
 */
export function getAssociatedColorByName(name, variant) {
    if (variant === 'group') {
        return groupColors[name]
    }
    if (variant === 'facility') {
        return facilityColors[name]
    } else return '#ccc'
}

/**
 * Parses incoming backend graph data and decides whether to merge a given group / facility into an "Other" category for the Pie chart. 
 * This function attempts to solve the SVG element's shortcoming of text wrapping when a pie slice is too small.
 * @param {Array} data Incoming data from the backend
 * @param {number} thresholdPercent Value that determines the minimum percentage that the group / facility have have before it merges into the "Other" category.
 * @returns The data array that has been changed
 */
// export function combineSmallPieSlices(data, variant, thresholdPercent = 2.9) {
//     const total = data.reduce((sum, item) => sum + item.value, 0);
//     const threshold = total * (thresholdPercent / 100);


//     const largeSlices = [];
//     const smallSlicesNames = [];
//     let otherValue = 0;
//     const otherColor = "#cccccc"; // Choose a color for "Other"

//     data.forEach(item => {
//         if (item.value < threshold) {
//             const percent = (item.value / total) * 100
//             const roundedPercent = Number(percent.toFixed(2))
//             // Running total to put in the "Other" tooltip
//             otherValue += item.value;
//             // Adds an object to the array if the above condition is met. 
//             // These include the name, percent, and color for tooltip display purposes.
//             smallSlicesNames.push({ name: item.name, color: getAssociatedColorByName(item.name, variant), percent: roundedPercent, value: item.value })
//         } else {
//             largeSlices.push(item);
//         }
//     });

//     if (otherValue > 0) {
//         largeSlices.push({
//             name: "Other",
//             value: otherValue,
//             fill: otherColor,
//             originalNamesAndColors: smallSlicesNames,
//         });
//     }

//     return largeSlices;
// }

/** 
* Renders the label around the pie chart dynamically based on calculated angles
* @returns {JSX.Element} Returns a < text > element for styling. 
*/
export function renderCustomizedLabel({ cx, cy, midAngle, outerRadius, percent, payload, name }) {
    const payloadColour = payload.fill
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 22;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const roundedPercent = (percent * 100).toFixed(1)
    const thresholdPercent = 4.0

    return (
        <text
            x={x}
            y={y}
            fill={payloadColour}
            fontSize={16}
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
        >
            {parseInt(roundedPercent) > thresholdPercent ? `${name} ${roundedPercent}%` : null}
        </text>
    );
}

/** 
* Renders the label around the pie chart dynamically based on calculated angles
* @returns {JSX.Element} Returns a < text > element for styling. 
*/
export function fertilizerPieChartLabel({ cx, cy, midAngle, outerRadius, payload, name }) {
    const payloadColour = payload.fill
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 22;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percent = payload.percent
    // const roundedPercent = (percent * 100).toFixed(1)
    const thresholdPercent = 4.0

    return (
        <text
            x={x}
            y={y}
            fill={payloadColour}
            fontSize={16}
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
        >
            {parseInt(percent) > thresholdPercent ? `${name} ${percent}%` : null}
        </text>
    );
}

/** 
* Defines the styling of the bar graph and stackable bar graph tooltips.
* - Note: Will render "N/A" in the totals section if the total is NaN, otherwise the total value itself.
* @param {Object} props All passed by Recharts except reversed.
* @param {boolean} [props.reversed] Optional argument. Reverses the order of the groups/facilities on the tooltip
* @returns {JSX.Element} Returns a nested < div > element
*/
export function BarGraphTooltip({ active, payload, label, reversed }) {
    if (active && payload && payload.length) {
        // const month = payload[0].payload.month
        const totals = payload[0].payload.total
        const filteredPayload = payload.filter(entry => entry.dataKey !== 'total');
        const orderPayload = (shouldReverse) => {
            return shouldReverse ? filteredPayload.slice().reverse() : filteredPayload
        }
        const orderedPayload = orderPayload(reversed)
        return (
            <Box sx={tooltipContainer}>
                <Typography sx={tooltipText}>
                    {label}:
                </Typography>
                {orderedPayload.map((payloadItem, index) => (
                    <Typography
                        key={index}
                        sx={{ color: payloadItem.color }}>
                        {payloadItem.name}: {payloadItem.value}
                    </Typography>
                ))}
                <Typography sx={{ fontWeight: 'bold' }}>
                    Total: {isNaN(totals)
                        ? 'N/A'
                        : totals}
                </Typography>
            </Box>
        );
    }
    return null;
}

/** 
* Defines the styling of the pie chart tooltips.
* @param {number} unit Optional argument, passed to make the piechart tooltip more informative
* @returns {JSX.Element} This is a nested < div > element, used for styling purposes.
*/
export function PieTooltip({ active, payload, unit }) {
    if (active && payload && payload.length) {
        return (
            <Box sx={tooltipContainer}>
                {payload.map((payloadItem, index) => {
                    return (
                        <Box key={index}>
                            <Typography sx={{
                                color: payloadItem.payload.fill,
                                ...tooltipText
                            }}>
                                {payloadItem.name}:
                            </Typography>
                            <Typography> Annual {unit}: {payloadItem.value}</Typography>
                        </Box>
                    )
                })
                }
            </Box >
        );
    }
    return null;
}

/**
 * Finds the nearest 'nice' number to match the Y axis total to e.g. a dynamically calculated power of 10
 * @param {number} axisMax This is passed as an argument by Recharts. More specifically, an array of length 2 [axisMin, axisMax] is passed. 
 * We only use axisMax, since axisMin is always 0 for our use case.
 * @returns {Array} An array of two numbers, since Recharts documentation specifically states that this is the shape required
 */
export function formatGraphAxis(axisMax) {
    if (axisMax <= 10) {
        return [0, 10];
    }
    const magnitude = Math.pow(10, Math.floor(Math.log10(axisMax)));
    const base = Math.max(10, magnitude);
    return [0, Math.ceil(axisMax / base) * base];
}

/**
 * Turns a given proper name into snake case for request-response manipulation of data
 * - eg "Vinimark Trading" into "vinimark_trading"
 * - eg "Reyneke Wines" into "reyneke_wines"
 * @param {string} name A proper name, eg "Vinimark Trading"
 * @returns {string} A snake case string, eg "vinimark_trading"
 */
export function formatNameToSnakeCaseName(name) {
    return name.replace(/ /g, '_').toLowerCase()
}

/**
 * Turns a given snake case into a proper name for graph display purposes
 * - eg "vinimark_trading" into "Vinimark Trading"
 * - eg "reyneke_wines" into "Reyneke Wines"
 * @param {string} snakeCaseName
 * @returns {string}
 */
export function formatSnakeCaseNameToDisplayName(snakeCaseName) {
    return snakeCaseName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

/**
 * Dynamically calculates an offset because the y-axis of the graphs
 * can become too squeezed if the number of digits exceeds 5
 * @param {Array} data 
 * @returns {number} Some negative number which will be passed as the offset to the graph's y-axis
 */
export function generateDynamicOffset(data) {
    const highestNumber = data?.reduce((runningTotal, dataObject) => {
        return dataObject.total > runningTotal ? dataObject.total : runningTotal
    }, 0)
    const numberOfDigits = highestNumber?.toString().length
    // not ideal with the magic numbers, but that's styling for you
    const baseOffset = -numberOfDigits
    // if the number is 5 digits i.e. 10,000 - we will need a higher offset
    if (numberOfDigits <= 7) {
        return baseOffset - (4 * numberOfDigits)
    } else return baseOffset - ((4 * numberOfDigits) + numberOfDigits)
}

/**
 * If there is no data in the data object in a respective group, remove the barKey 
 * so that its' colour does not show in the graph
 * @param {Array} barKeys - Array of snake case strings
 * @param {Array} data - Array of an API response
 * @returns A filtered array of barKeys
 */
export function filterBarKeysIfNoData(barKeys, data) {
    return barKeys?.filter(barKey => {
        // Sum all values for this barKey across the data array
        const total = data?.reduce((runningTotal, dataObject) => {
            // If the value exists and is a number, add it
            const value = Number(dataObject[barKey]);
            return runningTotal + (isNaN(value) ? 0 : value);
        }, 0);
        // Only include barKey if total is greater than 0
        return total > 0;
    });
}

/**
 * Function that takes an incoming response data array and parses it to see if the total is 0
 * - Note: The data shape for bar graphs vs stacked bar graphs is different, hence the variants
 * @param {Array} data - Array of an API response
 * @param {string} variant "bar-graph" or "stacked-bar-graph"
 * @returns A boolean value
 */
export function isDataSetEmpty(data, variant) {
    if (Array.isArray(data) && data.length === 0) {
        return true;
    }
    else if (variant === 'bar-graph') {
        return Array.isArray(data) &&
            data.length > 0 &&
            data.every((item) => Object.entries(item)
                .filter(([key]) => key !== 'month')
                .every(([, value]) => value === 0));
    }
    else if (variant === 'stacked-bar-graph') {
        return Array.isArray(data) &&
            data.length > 0 &&
            data.every((dataObject) => dataObject.total === 0)
    }
    else if (variant === 'fertilizer' || variant === 'agrochemicals') {
        const isEmpty = Array.isArray(data) &&
            data.length > 0 &&
            data.every((dataObject) => dataObject.total === 0)
        return isEmpty
    }
}

export function FertilizerLegend({ legendItems }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            {legendItems?.map(item => (
                <Box key={item.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: item.color, borderRadius: '4px' }} />
                    <Typography sx={{ color: item.color }}>{item.value}</Typography>
                </Box>
            ))}
        </Box>
    );
}

export function GroupLegend({ barKeys }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            {barKeys?.map((barKey, index) => {
                const color = groupColors[barKey]
                const name = barKey
                return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: '4px' }} />
                        <Typography sx={{ color: color }}>{name}</Typography>
                    </Box>
                )
            })}
        </Box>
    )
}

export function CustomLegend({ payload, variant }) {
    if (payload && payload.length) {
        return (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                {payload.map((payloadItem, index) => {
                    // For the "Total" line, payloadItem.type will be "line"
                    let color, name, icon;
                    if (payloadItem.type === 'line') {
                        color = payloadItem.color || '#000';
                        name = payloadItem.value; // Should be "Total"
                        icon = (
                            <Box sx={{
                                width: 18,
                                height: 3,
                                bgcolor: color,
                                borderRadius: 1,
                                display: 'inline-block'
                            }} />
                        );
                    } else {
                        color = variant === 'facility'
                            ? facilityColors[payloadItem.value]
                            : groupColors[payloadItem.value];
                        name = payloadItem.value;
                        icon = (
                            <Box sx={{
                                width: 14,
                                height: 14,
                                bgcolor: color,
                                borderRadius: '4px',
                                display: 'inline-block'
                            }} />
                        );
                    }
                    return (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {icon}
                            <Typography sx={{ color: color }}>{name}</Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    }
    return null;
}

/**
 * 
 * @param {Object} props 
 * @param {boolean} props.active Argument passed by Recharts 
 * @param {Object} props.payload Argument passed by Recharts
 * @param {string} props.variant "facility" or "group" to homogenize styling
 * @returns {JSX.Element}
 */
export function FertilizerTooltip({ active, payload, variant }) {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;

        return (
            <Box sx={tooltipContainer}>
                <Typography sx={tooltipText}>
                    {dataPoint.category}:
                </Typography>
                {variant === 'group' &&
                    <>
                        <Typography sx={{ color: fertilizerColors['Organic'] }}>Organic: {dataPoint.organic}</Typography>
                        <Typography sx={{ color: fertilizerColors['Synthetic'] }}>Synthetic: {dataPoint.synthetic}</Typography>
                    </>
                }
                <Typography sx={{ fontWeight: 'bold' }}>Total: {dataPoint.total}</Typography>
            </Box>
        );
    }
    return null;
}


/**
 * 
 * @param {Object} props 
 * @param {boolean} props.active Argument passed by Recharts 
 * @param {Object} props.payload Argument passed by Recharts
 * @param {string} props.variant "facility" or "group" to homogenize styling
 * @returns {JSX.Element}
 */
export function AgrochemicalsTooltip({ active, payload, variant }) {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;

        return (
            <Box sx={tooltipContainer}>
                <Typography sx={tooltipText}>
                    {dataPoint.category}:
                </Typography>
                {variant === 'group' &&
                    <>
                        <Typography sx={{ color: agrochemicalsColors['Herbicide'] }}>Herbicide: {dataPoint.herbicide}</Typography>
                        <Typography sx={{ color: agrochemicalsColors['Pesticide'] }}>Pesticide: {dataPoint.pesticide}</Typography>
                        <Typography sx={{ color: agrochemicalsColors['Insecticide'] }}>Insecticide: {dataPoint.insecticide}</Typography>
                    </>
                }
                <Typography sx={{ fontWeight: 'bold' }}>Total: {dataPoint.total}</Typography>
            </Box>
        );
    }
    return null;
}
